import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { UnifiedQuoteRow, QuoteResponder } from "@/hooks/useDirectQuoteRequests";
import { UserAvatar } from "@/components/common/UserAvatar";
import { ArrowRight, Pencil, Copy, Trash2, ArrowUpRight, BarChart3 } from "lucide-react";

interface Props {
  rows: UnifiedQuoteRow[];
  onOpen: (r: UnifiedQuoteRow) => void;
  onEdit: (r: UnifiedQuoteRow) => void;
  onDuplicate: (r: UnifiedQuoteRow) => void;
  onDelete: (r: UnifiedQuoteRow) => void;
  onConvert: (r: UnifiedQuoteRow) => void;
  onAnalytics: (r: UnifiedQuoteRow) => void;
}


function fmtMoney(n: number | null) {
  if (n == null) return "—";
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtTimeLeft(deadline: string) {
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return { txt: "Expired", cls: "text-rose-600" };
  const h = Math.floor(ms / 3600000);
  const cls = h < 4 ? "text-amber-600" : "text-foreground";
  if (h < 1) return { txt: `${Math.floor(ms / 60000)}m`, cls };
  if (h < 48) return { txt: `${h}h ${Math.floor((ms % 3600000) / 60000)}m`, cls };
  return { txt: `${Math.floor(h / 24)}d`, cls };
}
function statusPill(r: UnifiedQuoteRow) {
  const now = Date.now();
  const isExpired = new Date(r.deadline).getTime() <= now;
  if (r.converted_to_load_id) return { label: "Converted", cls: "bg-violet-50 text-violet-700 border-violet-200" };
  if (isExpired) return { label: "Expired", cls: "bg-rose-50 text-rose-700 border-rose-200" };
  const s = (r.status || "").toLowerCase();
  if (s === "posted" || s === "open") return { label: "Open", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (s === "awarded") return { label: "Awarded", cls: "bg-blue-50 text-blue-700 border-blue-200" };
  if (s === "pending") return { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: r.status || "—", cls: "bg-muted text-muted-foreground border-border" };
}

/**
 * Broker avatar bubbles for the shipper's My Quotes → Quotes column ONLY.
 *
 * LR Direct is an identified network (not blind-bid): shippers are expected
 * to see broker identity on their own quote requests. Do NOT reuse this in
 * marketplace / carrier surfaces where the blind-bid rule applies.
 *
 * Up to 5 avatars; anything beyond becomes a `+N` chip. `count` and
 * `responders` reference the same status set (active|awarded) — enforced by
 * matching `statsFor()` and the `get_shipper_quote_responders` RPC.
 */
function BrokerAvatarStack({ responders, count }: { responders: QuoteResponder[]; count: number }) {
  if (count === 0) return <span className="text-[11px] text-muted-foreground">No quotes yet</span>;
  const MAX = 5;
  const shown = responders.slice(0, MAX);
  const extra = Math.max(count - shown.length, 0);
  return (
    <div className="flex items-center">
      <div className="flex -space-x-1.5">
        {shown.map((b) => (
          <UserAvatar
            key={b.id}
            path={b.avatar_path}
            name={b.name}
            size="sm"
            className="ring-2 ring-background"
          />
        ))}
      </div>
      {extra > 0 && (
        <span className="ml-1.5 text-[11px] font-medium text-muted-foreground">+{extra}</span>
      )}
    </div>
  );
}

export function QuoteRequestsTable({ rows, onOpen, onEdit, onDuplicate, onDelete, onConvert, onAnalytics }: Props) {
  if (rows.length === 0) {
    return (
      <div className="p-10 text-center text-sm text-muted-foreground border rounded-md bg-card">
        No quotes match this view.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border rounded-md bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
            <th className="text-left font-bold px-4 py-2.5">Lane / Request Details</th>
            <th className="text-left font-bold px-3 py-2.5">Quotes</th>
            <th className="text-left font-bold px-3 py-2.5">Best Quote</th>
            <th className="text-left font-bold px-3 py-2.5">Spread</th>
            <th className="text-left font-bold px-3 py-2.5">Time Left</th>
            <th className="text-left font-bold px-3 py-2.5">Status</th>
            <th className="text-right font-bold px-4 py-2.5">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const t = fmtTimeLeft(r.deadline);
            const s = statusPill(r);
            const spread = r.low != null && r.high != null ? (r.high as number) - (r.low as number) : null;
            const spreadPct =
              spread != null && r.low && (r.low as number) > 0 ? (spread / (r.low as number)) * 100 : null;
            const isOpenLike = s.label === "Open";
            const canEdit = r.source === "load" && !r.converted_to_load_id;
            const canConvert = isOpenLike && !r.converted_to_load_id && r.response_count > 0;
            return (
              <tr
                key={`${r.source}-${r.id}`}
                className="border-b last:border-b-0 hover:bg-muted/40 transition-colors cursor-pointer"
                onClick={() => onOpen(r)}
              >
                <td className="px-4 py-3 align-top">
                  <div className="font-semibold text-foreground text-[13px]">
                    {r.origin_city}, {r.origin_state}
                    <ArrowRight className="inline h-3 w-3 mx-1.5 text-muted-foreground" />
                    {r.destination_city}, {r.destination_state}
                  </div>
                  <div className="flex items-center flex-wrap gap-1 mt-1">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{r.equipment_type}</Badge>
                    {r.converted_to_load_id && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-violet-50 text-violet-700 border-violet-200">
                        Converted
                      </Badge>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Posted: {new Date(r.created_at).toLocaleString(undefined, {
                      month: "2-digit", day: "2-digit", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="font-bold text-foreground text-base leading-none">{r.response_count}</div>
                  <div className="text-[10px] text-muted-foreground mb-1.5">Quotes</div>
                  <BrokerAvatarStack responders={r.responders} count={r.response_count} />
                </td>
                <td className="px-3 py-3 align-top">
                  <div className={`font-bold text-[13px] ${r.low != null ? "text-emerald-600" : "text-muted-foreground"}`}>
                    {fmtMoney(r.low)}
                  </div>
                  {/* Broker name intentionally not shown at list level — appears on detail. */}
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="font-medium text-foreground">{fmtMoney(spread)}</div>
                  {spreadPct != null && (
                    <div className="text-[10px] text-muted-foreground">{spreadPct.toFixed(0)}%</div>
                  )}
                </td>
                <td className={`px-3 py-3 align-top font-medium ${t.cls}`}>{t.txt}</td>
                <td className="px-3 py-3 align-top">
                  <Badge variant="outline" className={`text-[10px] ${s.cls}`}>{s.label}</Badge>
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="icon" variant="ghost" className="h-8 w-8"
                      title="Lane analytics" onClick={() => onAnalytics(r)}
                    >
                      <BarChart3 className="h-4 w-4" />
                    </Button>
                    {canConvert && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-brand-teal" title="Convert to load" onClick={() => onConvert(r)}>
                        <ArrowUpRight className="h-4 w-4" />
                      </Button>
                    )}

                    <Button
                      size="icon" variant="ghost" className="h-8 w-8"
                      title={canEdit ? "Edit" : "Cannot edit after quotes submitted"}
                      disabled={!canEdit}
                      onClick={() => onEdit(r)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Duplicate" onClick={() => onDuplicate(r)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive"
                      title="Delete" onClick={() => onDelete(r)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default QuoteRequestsTable;
